import React, { useContext, useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Card,
  Box,
  LinearProgress,
  Avatar,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoadingContext } from '../../context/LoadingContext';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'; // Certificate icon
import SchoolIcon from '@mui/icons-material/School'; // Course icon
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled'; // Session icon
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Completed session icon
import Confetti from 'react-confetti'; // Confetti animation

const StudentProgress = () => {
  const { setLoading } = useContext(LoadingContext);
  const [tabValue, setTabValue] = useState(0); // State for active tab
  const [showConfetti, setShowConfetti] = useState(false); // State for confetti animation
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check for mobile screens

  // Define state for courses by category
  const [coursesByCategory, setCoursesByCategory] = useState([
    {
      category: 'Free Trial',
      courses: [
        {
          id: 1,
          title: 'App Development Basics',
          sessions: [{ id: 1, name: 'Session 1', completed: false }],
          progress: 0,
        },
      ],
    },
    {
      category: 'Grade 7-8',
      courses: [
        {
          id: 2,
          title: 'Introduction to Web Development',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 3,
          title: 'HTML & CSS Fundamentals',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 4,
          title: 'JavaScript for Beginners',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 5,
          title: 'Responsive Web Design',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
      ],
    },
    {
      category: 'Grade 9-10',
      courses: [
        {
          id: 6,
          title: 'Introduction to Mobile Development',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 7,
          title: 'Flutter for Beginners',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 8,
          title: 'React Native Basics',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 9,
          title: 'Advanced Mobile Development',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
      ],
    },
    {
      category: 'Grade 11-12',
      courses: [
        {
          id: 10,
          title: 'Introduction to AI',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 11,
          title: 'Machine Learning Basics',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 12,
          title: 'Data Analysis with Python',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
        {
          id: 13,
          title: 'AI in Real-World Applications',
          sessions: Array.from({ length: 12 }, (_, i) => ({
            id: i + 1,
            name: `Session ${i + 1}`,
            completed: false,
          })),
          progress: 0,
        },
      ],
    },
  ]);

  // Simulate fetching data
  useEffect(() => {
    setLoading(true); // Show loading screen
    const fetchData = async () => {
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate 2-second delay
      setLoading(false); // Hide loading screen
    };

    fetchData();
  }, [setLoading]);

  // Handle session completion
  const handleSessionComplete = (courseId, sessionId, categoryIndex) => {
    setCoursesByCategory((prevCourses) =>
      prevCourses.map((category, index) =>
        index === categoryIndex
          ? {
              ...category,
              courses: category.courses.map((course) =>
                course.id === courseId
                  ? {
                      ...course,
                      sessions: course.sessions.map((session) =>
                        session.id === sessionId ? { ...session, completed: true } : session
                      ),
                      progress: Math.floor(
                        (course.sessions.filter((s) => s.completed).length + 1) *
                          (100 / course.sessions.length)
                      ),
                    }
                  : course
              ),
            }
          : category
      )
    );

    // Show confetti if the course is completed
    const course = coursesByCategory[categoryIndex].courses.find((c) => c.id === courseId);
    if (course.sessions.filter((s) => s.completed).length + 1 === course.sessions.length) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000); // Hide confetti after 5 seconds
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 5 }, position: 'relative' }}>
      {/* Confetti Animation */}
      {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} />}

      {/* Progress Header */}
      <Typography
        variant="h4"
        sx={{
          fontFamily: 'Fredoka One',
          color: '#004aad',
          mb: { xs: 2, sm: 3 },
          textAlign: 'center',
          fontSize: { xs: '1.5rem', sm: '2rem' }, // Responsive font size
        }}
      >
        My Learning Journey
      </Typography>

      {/* Tabs for Categories */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
          allowScrollButtonsMobile
          centered={!isMobile}
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: '0.8rem', sm: '1rem' }, // Responsive tab font size
              minWidth: 'unset', // Allow tabs to shrink on mobile
              padding: { xs: '6px 8px', sm: '12px 16px' }, // Responsive padding
            },
          }}
        >
          <Tab label="Free Trial" />
          <Tab label="Grade 7-8" />
          <Tab label="Grade 9-10" />
          <Tab label="Grade 11-12" />
        </Tabs>
      </Box>

      {/* Course Cards for Selected Category */}
      {coursesByCategory[tabValue].courses.map((course, courseIndex) => {
        // Check if the course is enabled (previous course is completed)
        const isCourseEnabled =
          courseIndex === 0 ||
          coursesByCategory[tabValue].courses[courseIndex - 1].progress === 100;

        return (
          <Card
            key={course.id}
            sx={{
              padding: { xs: 2, sm: 3 }, // Responsive padding
              backgroundColor: '#ffffff',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              mb: 3,
              borderRadius: '16px',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': { transform: 'scale(1.02)' },
              opacity: isCourseEnabled ? 1 : 0.5,
              pointerEvents: isCourseEnabled ? 'auto' : 'none',
            }}
          >
            {/* Course Title and Icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: '#004aad', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                <SchoolIcon sx={{ color: '#fff', fontSize: { xs: '1rem', sm: '1.5rem' } }} />
              </Avatar>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Fredoka One',
                  color: '#004aad',
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }, // Responsive font size
                }}
              >
                {course.title}
              </Typography>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Fredoka One',
                  color: '#004aad',
                  mb: 1,
                  fontSize: { xs: '0.9rem', sm: '1rem' }, // Responsive font size
                }}
              >
                Progress: {course.progress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={course.progress}
                sx={{
                  height: '8px', // Thinner progress bar on mobile
                  borderRadius: '5px',
                  backgroundColor: '#f0f0f0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#004aad',
                  },
                }}
              />
            </Box>

            {/* Sessions List */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Fredoka One',
                  color: '#004aad',
                  mb: 1,
                  fontSize: { xs: '1rem', sm: '1.2rem' }, // Responsive font size
                }}
              >
                Sessions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
                {course.sessions.map((session, index) => {
                  // Check if the session is enabled (previous session is completed)
                  const isSessionEnabled =
                    index === 0 || course.sessions[index - 1].completed;

                  return (
                    <Button
                      key={session.id}
                      variant="contained"
                      startIcon={session.completed ? <CheckCircleIcon /> : <PlayCircleFilledIcon />}
                      onClick={() => handleSessionComplete(course.id, session.id, tabValue)}
                      disabled={!isCourseEnabled || !isSessionEnabled}
                      sx={{
                        backgroundColor: session.completed ? '#4caf50' : '#004aad',
                        '&:hover': { backgroundColor: session.completed ? '#388e3c' : '#003a8c' },
                        fontFamily: 'Fredoka One',
                        textTransform: 'none',
                        fontSize: { xs: '0.8rem', sm: '1rem' }, // Responsive font size
                        padding: { xs: '6px 12px', sm: '8px 16px' }, // Responsive padding
                        borderRadius: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        opacity: isCourseEnabled && isSessionEnabled ? 1 : 0.5,
                        pointerEvents: isCourseEnabled && isSessionEnabled ? 'auto' : 'none',
                        flex: { xs: '1 1 40%', sm: 'unset' }, // Wrap buttons on mobile
                      }}
                    >
                      {session.name}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Get Certificate Button */}
            {course.progress === 100 && (
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<EmojiEventsIcon />}
                  sx={{
                    backgroundColor: '#ff9800',
                    '&:hover': { backgroundColor: '#f57c00' },
                    fontFamily: 'Fredoka One',
                    textTransform: 'none',
                    fontSize: { xs: '0.8rem', sm: '1rem' }, // Responsive font size
                    padding: { xs: '8px 16px', sm: '10px 20px' }, // Responsive padding
                    borderRadius: '8px',
                  }}
                >
                  Get Certificate
                </Button>
              </Box>
            )}
          </Card>
        );
      })}
    </Box>
  );
};

export default StudentProgress;